export type Currency =
  | "USD"
  | "AED"
  | "LKR"
  | "GBP"
  | "INR"
  | "AUD"
  | "CAD"
  | "SGD"
  | "MYR"
  | "THB"
  | "IDR"
  | "TZS";

/** One row per supported display currency. `digits` reflects the currency's
 *  typical magnitude for a card price: high-denomination units (rupiah,
 *  shilling, rupee, baht) read cleaner with no decimals; "hard" units keep 2.
 *  USD is the only one shown with its symbol — every other currency renders as
 *  an ISO code ("LKR 7,600"), matching the trader-reads-tickers ethos and
 *  sidestepping ambiguous/duplicate symbols ($ for CAD/AUD/SGD, Rs for INR). */
const CONFIG: Record<Currency, { locale: string; digits: 0 | 2 }> = {
  USD: { locale: "en-US", digits: 2 },
  AED: { locale: "en-AE", digits: 0 },
  LKR: { locale: "en-LK", digits: 0 },
  GBP: { locale: "en-GB", digits: 2 },
  INR: { locale: "en-IN", digits: 0 },
  AUD: { locale: "en-AU", digits: 2 },
  CAD: { locale: "en-CA", digits: 2 },
  SGD: { locale: "en-SG", digits: 2 },
  MYR: { locale: "en-MY", digits: 2 },
  THB: { locale: "en-TH", digits: 0 },
  IDR: { locale: "en-ID", digits: 0 },
  TZS: { locale: "en-TZ", digits: 0 },
};

export const CURRENCIES = Object.keys(CONFIG) as Currency[];

const CURRENCY_SET = new Set<string>(CURRENCIES);

/** Type guard for an unknown value (cookie, profile field, request body). */
export function isCurrency(value: unknown): value is Currency {
  return typeof value === "string" && CURRENCY_SET.has(value);
}

/** Coerce an unknown value to a Currency, falling back when unrecognised. */
export function parseCurrency(value: unknown, fallback: Currency = "USD"): Currency {
  return isCurrency(value) ? value : fallback;
}

function buildFormatter(currency: Currency, compact: boolean): Intl.NumberFormat {
  const { locale, digits } = CONFIG[currency];
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    ...(currency === "USD" ? {} : { currencyDisplay: "code" }),
    ...(compact
      ? { notation: "compact", maximumFractionDigits: 1 }
      : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
  });
}

const FORMATTERS = Object.fromEntries(
  CURRENCIES.map((c) => [c, buildFormatter(c, false)])
) as Record<Currency, Intl.NumberFormat>;

const COMPACT = Object.fromEntries(
  CURRENCIES.map((c) => [c, buildFormatter(c, true)])
) as Record<Currency, Intl.NumberFormat>;

/** Converts a USD amount and formats it for the selected currency.
 *  `compact` abbreviates 7-figure LKR values in tight tiles (LKR 7.6M). */
export function formatPrice(
  usd: number,
  currency: Currency,
  rates: Partial<Record<Currency, number>>,
  opts?: { compact?: boolean }
): string {
  const rate = currency === "USD" ? 1 : rates[currency];
  const effective: Currency = rate ? currency : "USD";
  const amount = usd * (rate ?? 1);
  if (opts?.compact && amount >= 1_000_000) return COMPACT[effective].format(amount);
  return FORMATTERS[effective].format(amount);
}

export function formatPercent(pct: number | null | undefined): string {
  if (pct == null) return "—";
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "±";
  return `${sign}${Math.abs(pct).toFixed(2)}%`;
}

export function relativeTime(date: Date | string | null | undefined): string {
  if (!date) return "unknown";
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return mins <= 1 ? "just now" : `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/** Volatility badge from coefficient of variation (30d). */
export function volatilityLabel(cov: number | null | undefined): {
  label: string;
  tone: "stable" | "moderate" | "volatile";
} | null {
  if (cov == null) return null;
  const pct = cov * 100;
  if (pct < 3) return { label: "Stable", tone: "stable" };
  if (pct <= 8) return { label: "Moderate", tone: "moderate" };
  return { label: "Volatile", tone: "volatile" };
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
